<template>
    <div class="modal-backdrop" @click.self="closeModal">
        <div class="relative max-h-full overflow-auto bg-white dark:bg-n-slate-2 shadow-md modal-container rtl:text-right skip-context-menu rounded-xl w-[600px]">
            <div class="modal-header border-b-2 border-n-slate-4 dark:border-n-slate-7">
                <h3 class="modal-title">{{ state === 'delete' ? 'Desconectar' : (isViewOnly ? 'Visualizar Inbox' : 'Editar Inbox') }}</h3>
                <button
                    class="z-10 ltr:right-2 rtl:left-2 top-2 inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 text-n-slate-12 hover:enabled:bg-n-alpha-2 focus-visible:bg-n-alpha-2 outline-transparent h-10 w-10 p-0 text-sm font-medium justify-center"
                    @click="closeModal">
                    <span class="i-lucide-x flex-shrink-0"></span>
                </button>
            </div>

            <!-- Normal edit mode -->
            <div v-if="state !== 'delete'" class="modal-body bg-n-slate-1">
                <div v-if="loading" class="text-center py-4">
                    <span class="animate-spin i-lucide-loader inline-block mr-2"></span>
                    Carregando dados...
                </div>

                <div v-else>
                    <!-- Summary section like AgentViewModal -->
                        <div class="flex items-center gap-4 mb-5 relative">
                            <div
                                class="h-14 w-14 rounded-full flex items-center justify-center text-white text-xl font-semibold"
                                :class="inboxIconBackgroundClass"
                                :style="inboxIconBackgroundStyle"
                            >
                                <span :class="inboxIconClass" class="h-7 w-7"></span>
                            </div>
                            <div class="max-w-xs flex-1">
                                <!-- Display mode -->
                                <div v-if="!isEditingName" class="flex items-center gap-2">
                                    <h3
                                        class="text-lg font-medium text-n-slate-12 truncate px-2 -mx-2 rounded transition-colors duration-200"
                                        :class="canEditInboxName && !isViewOnly ? 'cursor-pointer hover:bg-n-slate-3 dark:hover:bg-n-slate-7' : ''"
                                        @click="startEditingName"
                                        :title="canEditInboxName && !isViewOnly ? 'Clique para editar o nome' : ''"
                                    >
                                        {{ formData.name || inboxName || 'Inbox' }}
                                    </h3>
                                    <button
                                        v-if="canEditInboxName && !isViewOnly"
                                        type="button"
                                        class="text-n-slate-11 hover:text-n-slate-12 transition-colors flex items-center"
                                        @click="startEditingName"
                                        title="Editar nome do inbox"
                                    >
                                        <span class="i-lucide-pencil h-4 w-4"></span>
                                    </button>
                                </div>
                                <!-- Edit mode -->
                                <input
                                    v-else
                                    ref="nameInputRef"
                                    v-model="editingName"
                                    type="text"
                                    class="text-lg font-medium text-n-slate-12 bg-white dark:bg-n-slate-2 border border-n-slate-4 dark:border-n-slate-7 rounded px-2 py-1 w-full focus:outline-none focus:ring-2 focus:ring-n-brand"
                                    maxlength="32"
                                    @keydown="handleNameKeydown"
                                    @blur="saveEditingName"
                                />
                                <p class="text-sm text-n-slate-11 truncate">
                                    {{ formattedPhone || 'Número não disponível' }}
                                </p>
                            </div>

                            <!-- Status da Agenda - Somente para agentes do tipo 3 (Agendamento) -->
                            <div v-if="formData.selectedAgentFlow === 3" class="flex items-end h-14">
                                <span v-if="isGoogleCalendarConnected" class="inline-flex items-center gap-2 px-3 py-1.5 bg-n-teal-2 text-n-teal-11 rounded-full text-sm">
                                    <i class="i-lucide-calendar-check text-n-teal-9"></i>
                                    Agenda vinculada
                                </span>
                                <router-link
                                    v-else
                                    to="/app/settings/integrations/googleCalendar"
                                    class="inline-flex items-center gap-2 px-3 py-1.5 bg-n-amber-2 text-n-amber-11 rounded-full text-sm hover:bg-n-amber-3 transition-colors cursor-pointer no-underline"
                                >
                                    <i class="i-lucide-calendar-x text-n-amber-9"></i>
                                    Agenda não vinculada - Clique para configurar
                                </router-link>
                            </div>
                        </div>
                        
                        <div class="grid grid-cols-2 gap-5 text-sm">
                            <div class="bg-white dark:bg-n-slate-2 p-3 rounded-md">
                                <p class="text-n-slate-11 mb-1">Tipo de Integração</p>
                                <p class="font-medium text-n-slate-12 truncate">
                                    {{ formatIntegrationType(integration_type) }}
                                </p>
                            </div>
                            <div class="bg-white dark:bg-n-slate-2 p-3 rounded-md relative">
                                <p class="text-n-slate-11 mb-1">Agente IA</p>
                                <div
                                    ref="agentTrigger"
                                    class="flex items-center rounded transition-colors duration-200 -mx-1 px-1 py-1"
                                    :class="isAgentSelectDisabled || !canEditInboxAgent || isViewOnly ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-n-slate-1 dark:hover:bg-n-slate-3'"
                                    @click="toggleAgentSelect"
                                    @keydown="handleAgentKeyDown"
                                    :tabindex="isAgentSelectDisabled || !canEditInboxAgent || isViewOnly ? -1 : 0"
                                >
                                    <p class="font-medium text-n-slate-12 truncate mb-0 mr-1 flex-1">
                                        {{ isAgentSelectDisabled ? 'Não disponível para Messenger' : (formData.selectedAgent || 'Sem agente') }}
                                    </p>
                                    <Button
                                        type="button"
                                        @click.stop="toggleAgentSelect"
                                        icon="i-lucide-chevron-down"
                                        size="sm"
                                        color="slate"
                                        variant="ghost"
                                        class="!w-5 !h-5 min-w-0 pointer-events-none"
                                    />
                                </div>
                            </div>
                            
                            <!-- Agent Select Dropdown - Teleported -->
                            <Teleport to="body">
                                <div
                                    v-if="showAgentSelect"
                                    v-on-clickaway="closeAgentSelect"
                                    class="dropdown-pane dropdown-pane--open absolute w-48 bg-white dark:bg-n-slate-2 shadow-lg rounded-lg z-[10050] border border-n-slate-4 dark:border-n-slate-7 h-64 overflow-y-auto agent-suggestions-dropdown"
                                    :style="agentDropdownStyles"
                                >
                                    <WootDropdownMenu class="mb-0">
                                        <WootDropdownItem
                                            :class="{
                                                'bg-n-iris-50 dark:bg-n-iris-900/20': selectedAgentIndex === -1
                                            }"
                                            @mouseenter="selectedAgentIndex = -1"
                                        >
                                            <Button
                                                label="Sem agente"
                                                ghost
                                                slate
                                                sm
                                                start
                                                icon="i-lucide-ban"
                                                class="w-full"
                                                @click="selectAgent('')"
                                            />
                                        </WootDropdownItem>
                                        <WootDropdownItem
                                            v-for="(agent, index) in agentsList"
                                            :key="agent.name"
                                            :class="{
                                                'bg-n-iris-50 dark:bg-n-iris-900/20': index === selectedAgentIndex,
                                                '!mb-0': index === agentsList.length - 1
                                            }"
                                            @mouseenter="selectedAgentIndex = index"
                                        >
                                            <Button
                                                :label="agent.name"
                                                ghost
                                                slate
                                                sm
                                                start
                                                icon="i-lucide-bot"
                                                class="w-full"
                                                @click="selectAgent(agent.name)"
                                            />
                                        </WootDropdownItem>
                                    </WootDropdownMenu>
                                </div>
                            </Teleport>

                            <div class="col-span-2 bg-white dark:bg-n-slate-2 p-3 rounded-md relative">
                                <div class="flex items-center justify-between mb-3">
                                    <p class="text-n-slate-11 mb-0">Atendentes</p>
                                    <div
                                        v-if="canManageInboxMembers && !isViewOnly"
                                        ref="attendantTrigger"
                                        class="flex items-center cursor-pointer rounded hover:bg-n-slate-1 dark:hover:bg-n-slate-3 transition-colors duration-200 px-2 py-1"
                                        @click="toggleAttendantSelect"
                                    >
                                        <span class="text-sm text-n-slate-12 mr-1">Adicionar</span>
                                        <Button
                                            type="button"
                                            @click.stop="toggleAttendantSelect"
                                            icon="i-lucide-plus"
                                            size="sm"
                                            color="slate"
                                            variant="ghost"
                                            class="!w-4 !h-4 min-w-0 pointer-events-none"
                                        />
                                    </div>
                                </div>
                                
                                <!-- Selected attendants display area (tags) -->
                                <div v-if="formData.assignedAttendants.length > 0" class="selected-attendants-container">
                                    <div
                                        v-for="(attendant, index) in formData.assignedAttendants"
                                        :key="attendant.id"
                                        class="attendant-tag"
                                        :class="{ 'cursor-pointer': canManageInboxMembers && !isViewOnly, 'cursor-default': !canManageInboxMembers || isViewOnly }"
                                        @click="canManageInboxMembers && !isViewOnly ? removeAssignedAttendant(index) : null"
                                    >
                                        <span class="attendant-name">{{ attendant.name }}</span>
                                        <span v-if="canManageInboxMembers && !isViewOnly" class="remove-attendant-btn">&times;</span>
                                    </div>
                                </div>
                                <div v-else class="no-attendants-selected">
                                    <p>Nenhum atendente selecionado</p>
                                </div>
                            </div>
                            
                            <!-- Attendant Select Dropdown - Teleported -->
                            <Teleport to="body">
                                <div
                                    v-if="showAttendantSelect"
                                    v-on-clickaway="closeAttendantSelect"
                                    class="dropdown-pane dropdown-pane--open absolute w-48 bg-white dark:bg-n-slate-2 shadow-lg rounded-lg z-[10050] border border-n-slate-4 dark:border-n-slate-7"
                                    :style="attendantDropdownStyles"
                                >
                                    <WootDropdownMenu class="mb-0">
                                        <WootDropdownItem v-for="attendant in availableAttendants" :key="attendant.id">
                                            <Button
                                                :label="attendant.name"
                                                ghost
                                                slate
                                                sm
                                                start
                                                icon="i-lucide-user"
                                                class="w-full"
                                                @click="addAssignedAttendantFromDropdown(attendant)"
                                            />
                                        </WootDropdownItem>
                                    </WootDropdownMenu>
                                </div>
                            </Teleport>
                        </div>

                </div>
            </div>

            <!-- Delete confirmation mode -->
            <div v-else class="modal-body">
                <div class="confirmation-text py-4">
                    <p class="text-n-slate-12 mb-4 text-base">
                        Tem certeza que deseja desconectar este inbox? Esta ação removerá completamente as integrações e configurações.
                    </p>
                    <div v-if="scheduledMessagesCount > 0" class="bg-n-amber-2 border border-n-amber-6 rounded-lg p-4 mb-4">
                        <div class="flex items-start gap-3">
                            <i class="i-lucide-alert-triangle text-n-amber-9 text-xl flex-shrink-0 mt-0.5"></i>
                            <div>
                                <p class="text-n-amber-11 font-semibold mb-2">
                                    Atenção: Mensagens Agendadas
                                </p>
                                <p class="text-n-slate-12 text-sm">
                                    Este inbox possui <strong>{{ scheduledMessagesCount }}</strong> mensagem{{ scheduledMessagesCount > 1 ? 'ns' : '' }} agendada{{ scheduledMessagesCount > 1 ? 's' : '' }}.
                                    Ao desconectar o inbox, todas as mensagens agendadas serão <strong>permanentemente excluídas</strong>.
                                </p>
                            </div>
                        </div>
                    </div>
                    <p class="text-n-ruby-11 font-medium text-base">
                        Esta ação não pode ser desfeita.
                    </p>
                </div>
            </div>

            <!-- Normal edit mode footer -->
            <div v-if="state !== 'delete'" class="modal-footer flex border-t-2 border-n-slate-4 dark:border-n-slate-7"
                :class="canDisconnectInbox ? 'justify-between' : 'justify-end'">
                <div v-if="canDisconnectInbox">
                    <button type="button"
                        class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-ruby-9 text-white hover:enabled:bg-n-ruby-10 focus-visible:bg-n-ruby-10 outline-transparent h-10 px-4 text-sm font-medium justify-center"
                        @click="state = 'delete'" :disabled="loading">
                        Desconectar
                    </button>
                </div>
                <div class="flex gap-3">
                    <button type="button"
                        class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-slate-9/10 text-n-slate-12 hover:enabled:bg-n-slate-9/20 focus-visible:bg-n-slate-9/20 outline-transparent h-10 px-4 text-sm font-medium justify-center"
                        @click="closeModal" :disabled="loading">
                        {{ isViewOnly ? 'Fechar' : 'Cancelar' }}
                    </button>
                    <button v-if="canSaveInbox" type="button"
                        class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-brand text-white hover:enabled:brightness-110 focus-visible:brightness-110 outline-transparent h-10 px-4 text-sm font-medium justify-center"
                        @click="saveInbox" :disabled="!isFormValid || loading">
                        <span v-if="loading" class="animate-spin i-lucide-loader inline-block mr-2"></span>
                        <span>{{ loading ? 'Salvando...' : 'Salvar' }}</span>
                    </button>
                </div>
            </div>

            <!-- Delete confirmation mode footer -->
            <div v-else class="modal-footer flex justify-end gap-3 border-t-2 border-n-slate-4 dark:border-n-slate-7">
                <button type="button"
                    class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-slate-9/10 text-n-slate-12 hover:enabled:bg-n-slate-9/20 focus-visible:bg-n-slate-9/20 outline-transparent h-10 px-4 text-sm font-medium justify-center"
                    @click="state = null" :disabled="loading">
                    Cancelar
                </button>
                <button type="button"
                    class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-ruby-9 text-white hover:enabled:bg-n-ruby-10 focus-visible:bg-n-ruby-10 outline-transparent h-10 px-4 text-sm font-medium justify-center"
                    @click="deleteInbox" :disabled="loading">
                    <span v-if="loading" class="animate-spin i-lucide-loader inline-block mr-2"></span>
                    <span>
                        {{ loading ? 'Processando...' : showScheduledWarning ? 'Confirmar e apagar agendadas' : 'Confirmar' }}
                    </span>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { useStore } from 'vuex';
import wootAPI from '@/v3/api/apiClient';
import { useAlert } from 'dashboard/composables';
import { useUserRole } from 'dashboard/composables/useUserRole';
import { emitter } from 'shared/helpers/mitt';
import WootDropdownItem from 'shared/components/ui/dropdown/DropdownItem.vue';
import WootDropdownMenu from 'shared/components/ui/dropdown/DropdownMenu.vue';
import Button from 'dashboard/components-next/button/Button.vue';

export default {
    components: {
        WootDropdownItem,
        WootDropdownMenu,
        Button,
    },
    props: {
        inboxName: {
            type: String,
            default: '',
        },
        inboxId: {
            type: Number,
            default: 0,
        },
        show: {
            type: Boolean,
            default: false,
        },
        integration_type: {
            type: String,
            default: '',
        },
        display_phone_number: {
            type: String,
            default: '',
        },
        connection_status: {
            type: Number,
            default: 0,
        },
        viewOnly: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['close', 'save', 'deleted'],
    setup(props, { emit }) {
        const store = useStore();
        const { isAdmin, isManager } = useUserRole();
        const loading = ref(false);
        const error = ref(null);
        const state = ref(null);
        const formData = ref({
            name: props.inboxName || 'Inbox',
            selectedAgent: '',
            selectedAgentFlow: null,
            assignedAttendants: []
        });
        const canEditInboxName = computed(() => isAdmin.value);
        const canEditInboxAgent = computed(() => isAdmin.value || isManager.value);
        const canManageInboxMembers = computed(() => isAdmin.value || isManager.value);
        const canDisconnectInbox = computed(() => isAdmin.value);
        const isViewOnly = computed(() => {
            if (props.viewOnly) return true;
            return !canEditInboxAgent.value && !canEditInboxName.value && !canManageInboxMembers.value;
        });
        const canSaveInbox = computed(() => {
            return !isViewOnly.value && (canEditInboxAgent.value || canEditInboxName.value || canManageInboxMembers.value);
        });
        const agentsList = ref([]);
        const instanceData = ref(null);
        const connectedPhone = ref(props.display_phone_number || '');
        const showAgentSelect = ref(false);
        const showAttendantSelect = ref(false);
        const selectedAgentIndex = ref(-1);
        const isGoogleCalendarConnected = ref(false);
        const scheduledMessagesCount = ref(0);
        const showScheduledWarning = ref(false);

        // Inline name editing
        const isEditingName = ref(false);
        const editingName = ref('');
        const nameInputRef = ref(null);
        
        // Template refs for dropdown positioning
        const agentTrigger = ref(null);
        const attendantTrigger = ref(null);
        
        // Para o campo de atendentes atribuídos
        const selectedAttendant = ref('');
        
        // Obter atendentes disponíveis do Vuex store
        const availableAttendants = computed(() => {
            return store.getters['agents/getAgents'] || [];
        });

        // Computed styles for teleported dropdowns
        const agentDropdownStyles = computed(() => {
            if (!agentTrigger.value || !showAgentSelect.value) return {};
            const rect = agentTrigger.value.getBoundingClientRect();
            return {
                left: `${rect.left}px`,
                top: `${rect.bottom + 2}px`
            };
        });

        const attendantDropdownStyles = computed(() => {
            if (!attendantTrigger.value || !showAttendantSelect.value) return {};
            const rect = attendantTrigger.value.getBoundingClientRect();
            return {
                left: `${rect.left}px`,
                top: `${rect.bottom + 2}px`
            };
        });

        const closeModal = () => {
            state.value = null;
            showScheduledWarning.value = false;
            scheduledMessagesCount.value = 0;
            showAgentSelect.value = false;
            showAttendantSelect.value = false;
            emit('close');
        };

        const isAgentSelectDisabled = computed(() => {
            return props.integration_type === 'messenger';
        });

        const toggleAgentSelect = () => {
            if (isAgentSelectDisabled.value || !canEditInboxAgent.value || isViewOnly.value) return;
            showAgentSelect.value = !showAgentSelect.value;
            if (showAgentSelect.value) {
                selectedAgentIndex.value = -1;
            }
        };

        const closeAgentSelect = () => {
            showAgentSelect.value = false;
        };

        const selectAgent = (agentName) => {
            if (!canEditInboxAgent.value || isViewOnly.value) return;
            formData.value.selectedAgent = agentName;

            // Encontrar o flow do agente selecionado
            const selectedAgentData = agentsList.value.find(a => a.name === agentName);
            formData.value.selectedAgentFlow = selectedAgentData ? selectedAgentData.flow : null;

            closeAgentSelect();
        };

        const handleAgentKeyDown = (e) => {
            if (!showAgentSelect.value) {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleAgentSelect();
                }
                return;
            }

            const totalOptions = agentsList.value.length + 1; // +1 para "Sem agente"
            
            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    selectedAgentIndex.value = Math.min(selectedAgentIndex.value + 1, agentsList.value.length - 1);
                    break;
                    
                case 'ArrowUp':
                    e.preventDefault();
                    selectedAgentIndex.value = Math.max(selectedAgentIndex.value - 1, -1);
                    break;
                    
                case 'Enter':
                    e.preventDefault();
                    if (selectedAgentIndex.value === -1) {
                        selectAgent('');
                    } else {
                        selectAgent(agentsList.value[selectedAgentIndex.value].name);
                    }
                    break;
                    
                case 'Escape':
                    closeAgentSelect();
                    break;
            }
        };

        const toggleAttendantSelect = () => {
            if (!canManageInboxMembers.value || isViewOnly.value) return;
            showAttendantSelect.value = !showAttendantSelect.value;
        };

        const closeAttendantSelect = () => {
            showAttendantSelect.value = false;
        };

        const addAssignedAttendantFromDropdown = (attendant) => {
            if (!canManageInboxMembers.value || isViewOnly.value) return;
            if (!attendant) return;
            
            // Verifica se o atendente já está selecionado
            const attendantExists = formData.value.assignedAttendants.some(
                existingAttendant => existingAttendant.id === attendant.id
            );
            
            if (!attendantExists) {
                formData.value.assignedAttendants.push(attendant);
            }
            
            closeAttendantSelect();
        };

        const isFormValid = computed(() => {
            // If currently editing name, validate the editing value
            if (isEditingName.value && editingName.value) {
                const trimmedEditingName = editingName.value.trim();
                return trimmedEditingName.length >= 3 && trimmedEditingName.length <= 32;
            }

            if (!canEditInboxName.value) return true;

            // Otherwise validate the saved name
            const trimmedName = formData.value.name ? formData.value.name.trim() : '';
            return trimmedName.length >= 3 && trimmedName.length <= 32;
        });

        const getCookie = (name) => {
            const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
            return match ? decodeURIComponent(match[2]) : null;
        };

        const getAuthHeaders = () => {
            const cookie = getCookie('cw_d_session_info');
            if (!cookie) return {};
            try {
                const sessionInfo = JSON.parse(cookie);
                return {
                    'access-token': sessionInfo['access-token'],
                    'client': sessionInfo['client'],
                    'uid': sessionInfo['uid']
                };
            } catch (e) {
                console.error('Error parsing cookie:', e);
                return {};
            }
        };

        // Função para verificar se o Google Calendar está conectado
        const checkGoogleCalendarConnection = async () => {
            try {
                const authHeaders = getAuthHeaders();

                const params = new URLSearchParams({
                    start: new Date().toISOString(),
                    end: new Date().toISOString()
                });

                const response = await fetch(`/api/v1/google_calendar?${params.toString()}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        ...authHeaders
                    },
                    credentials: 'include'
                });

                if (response.ok) {
                    isGoogleCalendarConnected.value = true;
                } else {
                    isGoogleCalendarConnected.value = false;
                }
            } catch (error) {
                isGoogleCalendarConnected.value = false;
            }
        };

        // Carrega os atendentes disponíveis
        const fetchAttendants = async () => {
            try {
                await store.dispatch('agents/get', {
                    params: { context: 'inbox_members' },
                });
            } catch (err) {
                console.error('Erro ao carregar atendentes:', err);
                error.value = 'Erro ao carregar lista de atendentes.';
            }
        };

        // Carrega os atendentes atribuídos ao inbox
        const fetchAssignedAttendants = async () => {
            if (!props.inboxId) return;

            try {
                const response = await store.dispatch('inboxMembers/get', {
                    inboxId: props.inboxId
                });

                if (response && response.data && response.data.payload) {
                    formData.value.assignedAttendants = response.data.payload;
                }
            } catch (err) {
                const status = err?.response?.status;
                if (status === 401 || status === 403) {
                    formData.value.assignedAttendants = [];
                    return;
                }

                console.error('Erro ao carregar atendentes atribuídos:', err);
                error.value = 'Erro ao carregar atendentes atribuídos.';
            }
        };

        const fetchEditData = async () => {
            if (loading.value || !props.inboxId) return;

            loading.value = true;
            error.value = null;

            try {
                // Carregar atendentes primeiro
                await fetchAttendants();

                // Carregar atendentes atribuídos
                await fetchAssignedAttendants();

                // Verificar status do Google Calendar
                await checkGoogleCalendarConnection();
                
                const headers = getAuthHeaders();
                const response = await wootAPI.get(
                    `/api/v1/inboxmeta/edit`,
                    {
                        params: { inbox_id: props.inboxId },
                        headers
                    }
                );

                // Update with received data
                agentsList.value = response.data.agents || [];
                formData.value.selectedAgent = response.data.selected_agent || '';
                formData.value.name = response.data.display_name || formData.value.name || props.inboxName || 'Inbox';
                instanceData.value = response.data.instances || [];

                // Debug: Verificar o que está sendo retornado
                console.log('Agents list from API:', response.data.agents);
                console.log('Selected agent:', response.data.selected_agent);

                // Se houver um agente selecionado, encontrar seu flow
                if (response.data.selected_agent) {
                    const selectedAgentData = agentsList.value.find(a => a.name === response.data.selected_agent);
                    formData.value.selectedAgentFlow = selectedAgentData ? selectedAgentData.flow : null;
                    console.log('Selected agent flow:', formData.value.selectedAgentFlow);
                }
                
                if (!connectedPhone.value) {
                    connectedPhone.value = response.data.phone_number || response.data.username || response.data.profile || '';
                }
                
            } catch (err) {
                console.error('Erro buscando dados do inbox', err);
                error.value = 'Erro interno do servidor.';
            } finally {
                loading.value = false;
            }
        };
        
        // Adiciona um atendente à lista de selecionados
        const addAssignedAttendant = () => {
            if (!canManageInboxMembers.value || isViewOnly.value) return;
            if (!selectedAttendant.value) return;
            
            // Verifica se o atendente já está selecionado
            const attendantExists = formData.value.assignedAttendants.some(
                attendant => attendant.id === selectedAttendant.value.id
            );
            
            if (!attendantExists) {
                formData.value.assignedAttendants.push(selectedAttendant.value);
            }
            
            // Limpa a seleção
            selectedAttendant.value = '';
        };
        
        // Remove um atendente da lista de selecionados
        const removeAssignedAttendant = (index) => {
            if (!canManageInboxMembers.value || isViewOnly.value) return;
            formData.value.assignedAttendants.splice(index, 1);
        };
        
        // Set initial form data from props
        watch(
            () => props.inboxName,
            name => {
                formData.value.name = name || 'Inbox';
            },
            { immediate: true }
        );

        // Reset state when modal is shown
        watch(
            () => props.show,
            isVisible => {
                if (isVisible) {
                    state.value = null;
                    showScheduledWarning.value = false;
                    scheduledMessagesCount.value = 0;
                    if (props.inboxId) {
                        fetchEditData();
                    }
                }
            },
            { immediate: true }
        );

        const saveInbox = async () => {
            if (loading.value || !isFormValid.value || !canSaveInbox.value) return;

            loading.value = true;
            error.value = null;

            if (editingName.value) {
                saveEditingName();
            }

            try {
                const headers = getAuthHeaders();
                let shouldReloadMetadata = false;

                if (canEditInboxAgent.value || canEditInboxName.value) {
                    // 1. Salvar as configurações do inbox
                    await wootAPI.post(
                        `/api/v1/inboxmeta/update_meta`,
                        {
                            agent: formData.value.selectedAgent,
                            name: formData.value.name,
                            inbox_id: props.inboxId,
                        },
                        { headers }
                    );
                    shouldReloadMetadata = true;
                }

                if (canManageInboxMembers.value) {
                    // 2. Salvar os atendentes atribuídos
                    const agentList = formData.value.assignedAttendants.map(attendant => attendant.id);
                    await store.dispatch('inboxMembers/create', {
                        inboxId: props.inboxId,
                        agentList,
                    });
                }

                if (shouldReloadMetadata) {
                    // 3. Recarrega metadata das inboxes para refletir hasAI/connected imediatamente
                    await store.dispatch('inboxMetadata/loadAllMetadata');
                }

                useAlert('Inbox atualizado com sucesso!');
                console.log('Emitting reload_agent_names event');
                emitter.emit('reload_agent_names');
                emit('save');
                closeModal();
            } catch (err) {
                console.error('Erro ao salvar o inbox', err);
                error.value = 'Ocorreu um erro ao salvar as alterações.';
                useAlert('Erro ao salvar as alterações');
            } finally {
                loading.value = false;
            }
        };

        const deleteInbox = async () => {
            if (loading.value) return;

            loading.value = true;
            error.value = null;

            try {
                const headers = getAuthHeaders();
                const deleteParams = {
                    inbox_id: props.inboxId,
                    integration_type: props.integration_type
                };

                // If we have scheduled messages and this is the confirmed deletion, add force_delete
                if (showScheduledWarning.value) {
                    deleteParams.force_delete = true;
                }

                await wootAPI.delete(
                `/api/v1/inboxmeta`,
                {
                    params: deleteParams,
                    headers,
                }
                );

                useAlert('Inbox desconectado com sucesso!');
                emit('deleted');
                closeModal();
            } catch (err) {
                // Check if error is about scheduled messages
                const errorData = err.response?.data;
                const scheduledCount = Number(errorData?.scheduled_messages_count || 0);
                if ((errorData?.requires_confirmation && scheduledCount > 0) || scheduledCount > 0) {
                    // Store the count and keep the modal in delete state to show warning
                    scheduledMessagesCount.value = scheduledCount;
                    showScheduledWarning.value = true;
                    state.value = 'delete';
                    loading.value = false;
                    useAlert(`Este inbox possui ${scheduledCount} mensagem(ns) agendada(s). Confirme para excluir tudo.`);
                    return; // Don't close modal, let user see the warning
                }

                // surface the backend error or fallback to message
                error.value = errorData?.message || errorData?.error || err.message;
                useAlert(error.value || 'Erro ao desconectar inbox');
            } finally {
                loading.value = false;
            }
        };

        const formatIntegrationType = (type) => {
            // Check for evolution type
            if (type === 'evolution') {
                return 'WhatsApp Não Oficial';
            }

            const typeMap = {
                'official': 'WhatsApp Oficial',
                'instagram': 'Instagram',
                'messenger': 'Messenger',
                'web_widget': 'Widget Web',
                'facebook': 'Facebook',
                'twitter': 'Twitter',
                'telegram': 'Telegram',
                'line': 'Line',
                'sms': 'SMS',
                'email': 'E-mail',
                'api': 'API'
            };
            return typeMap[type] || type || 'Não definido';
        };

        const formatConnectionStatus = (status) => {
            const statusMap = {
                'open': 'Conectado',
                'close': 'Desconectado',
                'connecting': 'Conectando...',
                '': 'Não configurado'
            };
            return statusMap[status] || statusMap[''];
        };

        // Format phone number for display
        const formattedPhone = computed(() => {
            const phone = connectedPhone.value;
            if (!phone) return '';

            // For Instagram and Messenger, return the profile as-is
            if (props.integration_type === 'instagram' || props.integration_type === 'messenger') {
                return phone;
            }

            // If it's an Instagram username (starts with @), return as-is
            if (phone.startsWith('@')) {
                return phone;
            }

            // Remove all non-digit characters
            const cleaned = phone.replace(/\D/g, '');

            // If it doesn't look like a phone number, return as-is
            if (cleaned.length < 10) {
                return phone;
            }

            // Format based on length
            if (cleaned.length >= 13) {
                // Format: +xx xx xxxxx-xxxx
                const countryCode = cleaned.substring(0, 2);
                const areaCode = cleaned.substring(2, 4);
                const firstPart = cleaned.substring(4, 9);
                const secondPart = cleaned.substring(9);
                return `+${countryCode} ${areaCode} ${firstPart}-${secondPart}`;
            } else {
                // Format: +xx xx xxxx-xxxx
                const countryCode = cleaned.substring(0, 2);
                const areaCode = cleaned.substring(2, 4);
                const firstPart = cleaned.substring(4, 8);
                const secondPart = cleaned.substring(8);
                return `+${countryCode} ${areaCode} ${firstPart}-${secondPart}`;
            }
        });

        // Computed properties for inbox icon and background
        const inboxIconClass = computed(() => {
            const type = props.integration_type;

            if (type === 'evolution' || type === 'official') {
                return 'i-lucide-message-circle';
            } else if (type === 'instagram') {
                return 'i-lucide-instagram';
            } else if (type === 'messenger') {
                return 'i-lucide-message-circle';
            }

            return 'i-lucide-inbox';
        });

        const inboxIconBackgroundClass = computed(() => {
            const type = props.integration_type;

            // Don't use bg class for Instagram (uses gradient style instead)
            if (type === 'instagram') {
                return '';
            }

            // Green for WhatsApp
            if (type === 'evolution' || type === 'official') {
                return 'bg-green-500';
            }

            // Blue for Messenger
            if (type === 'messenger') {
                return 'bg-n-brand';
            }

            // Blue for default/unknown
            return 'bg-n-brand';
        });

        const inboxIconBackgroundStyle = computed(() => {
            const type = props.integration_type;

            if (type === 'instagram') {
                return {
                    background: 'linear-gradient(45deg, rgb(240, 148, 51), rgb(230, 104, 60), rgb(220, 39, 67), rgb(204, 35, 102), rgb(188, 24, 136))'
                };
            }

            return {};
        });

        // Inline name editing functions
        const startEditingName = async () => {
            if (!canEditInboxName.value || isViewOnly.value) return;
            editingName.value = formData.value.name || '';
            isEditingName.value = true;
            
            // Wait for DOM update and then focus the input
            await nextTick();
            if (nameInputRef.value) {
                nameInputRef.value.focus();
                nameInputRef.value.select(); // Select all text for better UX
            }
        };

        const cancelEditingName = () => {
            isEditingName.value = false;
            editingName.value = '';
        };

        const saveEditingName = () => {
            const trimmedName = editingName.value.trim();
            
            // Validate name length
            if (trimmedName.length < 3 || trimmedName.length > 32) {
                // Don't save invalid names, just cancel editing
                cancelEditingName();
                return;
            }

            // Only update local formData, don't save to backend
            formData.value.name = trimmedName;
            
            // Exit edit mode
            isEditingName.value = false;
            editingName.value = '';
        };

        const handleNameKeydown = (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                saveEditingName();
            } else if (e.key === 'Escape') {
                e.preventDefault();
                cancelEditingName();
            }
        };

        onMounted(() => {
            // Carregar atendentes ao montar o componente
            fetchAttendants();
        });

        return {
            formData,
            closeModal,
            loading,
            error,
            isFormValid,
            agentsList,
            instanceData,
            saveInbox,
            connectedPhone,
            deleteInbox,
            state,
            showAgentSelect,
            toggleAgentSelect,
            closeAgentSelect,
            selectAgent,
            selectedAgentIndex,
            handleAgentKeyDown,
            isAgentSelectDisabled,
            // Template refs
            agentTrigger,
            attendantTrigger,
            // Dropdown positioning
            agentDropdownStyles,
            attendantDropdownStyles,
            // Campos para dropdown de atendentes
            showAttendantSelect,
            toggleAttendantSelect,
            closeAttendantSelect,
            addAssignedAttendantFromDropdown,
            // Campos para atendentes
            selectedAttendant,
            availableAttendants,
            addAssignedAttendant,
            removeAssignedAttendant,
            formatIntegrationType,
            formatConnectionStatus,
            // Inline name editing
            canEditInboxName,
            canEditInboxAgent,
            canManageInboxMembers,
            canDisconnectInbox,
            isViewOnly,
            canSaveInbox,
            isEditingName,
            editingName,
            nameInputRef,
            startEditingName,
            cancelEditingName,
            handleNameKeydown,
            // Google Calendar
            isGoogleCalendarConnected,
            // Scheduled messages
            scheduledMessagesCount,
            showScheduledWarning,
            // Phone formatting
            formattedPhone,
            // Inbox icon and background
            inboxIconClass,
            inboxIconBackgroundClass,
            inboxIconBackgroundStyle,
            // Props
            inboxName: props.inboxName,
            inboxId: props.inboxId,
            integration_type: props.integration_type,
            display_phone_number: props.display_phone_number,
            connection_status: props.connection_status,
            viewOnly: props.viewOnly
        };
    }
};
</script>

<style scoped>
:root {
    --light-primary-hover: #dddddd;
    --transition-speed: 0.2s;
}

/* Modal styles */
.modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.04);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    backdrop-filter: blur(4px);
}

.modal-container {
    border-radius: 8px;
    max-width: 90%;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 24px;
    border-bottom: 1px solid rgb(var(--slate-4) / 1);
}

.dark .modal-header {
    border-bottom: 1px solid rgb(var(--slate-6) / 1);
}

.modal-title {
    font-size: 18px;
    font-weight: 600;
    margin: 0;
}

.modal-body {
    padding: 24px;
}

.modal-footer {
    border-top: 1px solid rgb(var(--slate-4) / 1);
    padding: 16px 24px;
}

.dark .modal-footer {
    border-top: 1px solid rgb(var(--slate-6) / 1);
}

/* Form styles */
.form-group {
    margin-bottom: 20px;
}

.form-group label {
    display: block;
    margin-bottom: 6px;
    font-weight: 500;
}

.form-input {
    width: 100%;
    padding: 10px 12px 10px 8px;
    border: 1px solid rgb(var(--slate-4) / 1);
    border-radius: 8px;
}

.dark .form-input {
    border-color: rgb(var(--slate-7) / 1);
}

.form-input.border-red-500 {
    border-color: #ef4444;
}

.confirmation-text p {
    margin: 0;
    line-height: 1.5;
}

/* Estilos para o componente de tags de atendentes atribuídos */
.selected-attendants-container {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
}

.attendant-tag {
    display: flex;
    align-items: center;
    background-color: rgb(226, 232, 240);
    border-radius: 16px;
    padding: 4px 10px 4px 12px;
    cursor: pointer;
    transition: background-color 0.2s;
    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
}

.attendant-tag:hover {
    background-color: rgb(203, 213, 225);
}

.dark .attendant-tag {
    background-color: rgb(51, 65, 85);
}

.dark .attendant-tag:hover {
    background-color: rgb(71, 85, 105);
}

.attendant-name {
    font-size: 14px;
    color: rgb(15, 23, 42);
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.dark .attendant-name {
    color: rgb(241, 245, 249);
}

.remove-attendant-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    margin-left: 6px;
    color: rgb(100, 116, 139);
    font-size: 16px;
    padding: 0;
}

.attendant-tag:hover .remove-attendant-btn {
    color: rgb(15, 23, 42);
}

.dark .remove-attendant-btn {
    color: rgb(148, 163, 184);
}

.dark .attendant-tag:hover .remove-attendant-btn {
    color: rgb(226, 232, 240);
}

.no-attendants-selected {
    padding: 12px;
    background-color: rgb(241, 245, 249, 0.5);
    border-radius: 8px;
    text-align: center;
    margin-top: 8px;
}

.dark .no-attendants-selected {
    background-color: rgb(51, 65, 85, 0.3);
}

.no-attendants-selected p {
    color: rgb(100, 116, 139);
    font-size: 14px;
    margin: 0;
}

.dark .no-attendants-selected p {
    color: rgb(148, 163, 184);
}

/* Custom scrollbar styles - apenas vertical */
.agent-suggestions-dropdown {
    scrollbar-width: thin;
    scrollbar-color: rgb(203 213 225) transparent; /* n-slate-300 */
}

.agent-suggestions-dropdown::-webkit-scrollbar {
    width: 8px;
}

.agent-suggestions-dropdown::-webkit-scrollbar-track {
    background: transparent;
}

.agent-suggestions-dropdown::-webkit-scrollbar-thumb {
    background-color: rgb(203 213 225); /* n-slate-300 */
    border-radius: 4px;
}

.agent-suggestions-dropdown::-webkit-scrollbar-thumb:hover {
    background-color: rgb(148 163 184); /* n-slate-400 */
}

/* Dark mode scrollbar */
@media (prefers-color-scheme: dark) {
    .agent-suggestions-dropdown {
        scrollbar-color: rgb(71 85 105) transparent; /* n-slate-600 */
    }
    
    .agent-suggestions-dropdown::-webkit-scrollbar-thumb {
        background-color: rgb(71 85 105); /* n-slate-600 */
    }
    
    .agent-suggestions-dropdown::-webkit-scrollbar-thumb:hover {
        background-color: rgb(100 116 139); /* n-slate-500 */
    }
}
</style>
